import { RoleBadge } from "@/components/Shared";
import RosterSpendChart from "@/components/RosterSpendChart";

const ROLES = ["P", "D", "C", "A"];

/**
 * Shared live view of every active team's roster, with the user's own card highlighted.
 * Includes a per-role spend mini-chart below each roster header.
 */
export default function AllRosters({ activeTeams, myTeam, totalSlots }) {
  const filledTotal = activeTeams.reduce((s, t) => s + (t.players?.length || 0), 0);
  return (
    <div className="glass rounded-2xl p-5" data-testid="all-rosters">
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow">Rose live — tutti i partecipanti</div>
        <div className="text-xs text-slate-400">
          {filledTotal} / {activeTeams.length * totalSlots}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {activeTeams.map((t) => (
          <RosterCard key={t.id} team={t} isMe={t.id === myTeam?.id} totalSlots={totalSlots} />
        ))}
      </div>
    </div>
  );
}

function RosterCard({ team, isMe, totalSlots }) {
  const slotsFilled = team.players?.length || 0;
  const roleCounts = ROLES.map((r) => ({
    r, n: (team.players || []).filter((p) => p.role === r).length,
  }));
  const sorted = [...(team.players || [])].sort(
    (a, b) => ROLES.indexOf(a.role) - ROLES.indexOf(b.role),
  );
  return (
    <div data-testid={`roster-team-${team.id}`}
         className={`rounded-xl p-3 border ${isMe ? "border-emerald-500/60 bg-emerald-500/5" : "border-white/10 bg-slate-900/40"}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="min-w-0">
          <div className="font-black text-sm truncate flex items-center gap-1">
            {team.name}
            {isMe && <span className="text-[9px] uppercase tracking-widest text-emerald-400 font-black">tu</span>}
          </div>
          <div className="text-[10px] text-slate-500">{team.user_name || "—"}</div>
        </div>
        <div className="text-right">
          <div className="font-mono font-black text-emerald-400">
            {team.credits}<span className="text-[10px] text-slate-500 ml-1">FM</span>
          </div>
          <div className="text-[10px] text-slate-500">{slotsFilled}/{totalSlots}</div>
        </div>
      </div>

      <div className="flex gap-1 mb-1">
        {roleCounts.map(({ r, n }) => (
          <span key={r} className={`text-[9px] font-bold px-1.5 py-0.5 rounded role-${r}`}>{n}{r}</span>
        ))}
      </div>

      <RosterSpendChart players={team.players} testIdPrefix={`spend-${team.id}-`} />

      {slotsFilled === 0 ? (
        <div className="text-xs text-slate-500 italic mt-2">Nessun calciatore ancora</div>
      ) : (
        <div className="space-y-1 mt-2 max-h-56 overflow-y-auto scroll-thin pr-1">
          {sorted.map((p) => (
            <div key={p.player_id} className="flex items-center gap-2 p-1.5 rounded-md bg-slate-900/60 border border-white/5">
              <RoleBadge role={p.role} className="w-6 h-6 text-[9px]" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold truncate">{p.name}</div>
                <div className="text-[9px] text-slate-500 truncate">{p.team}</div>
              </div>
              <div className="font-mono text-[11px] font-bold text-emerald-400">{p.price}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
